import { NavigationState, PartialRoute } from '@react-navigation/native';
import { navigationRef } from 'components/Navigation/navigationRef';
import { noop } from 'lodash';
import React from 'react';

const cascade = (
  routes:
    | NavigationState['routes']
    | PartialRoute<NavigationState['routes'][number]>[],
  name: string,
  params?: Record<string, unknown>,
): boolean => {
  let found = false;

  for (const route of routes) {
    if (route.name === name) {
      if (params === undefined) {
        found = true;
        break;
      } else {
        const routeParams = route.params as Record<string, unknown> | undefined;
        if (routeParams) {
          let allMatch = true;

          for (const paramKey in params) {
            if (routeParams[paramKey] !== params[paramKey]) {
              allMatch = false;
              break;
            }
          }
          if (allMatch) {
            found = true;
            break;
          }
        }
      }
    }
    if (
      route.name === name &&
      (params === undefined ||
        Object.keys(params).every((paramKey) => {
          const routeParams = route.params as
            | Record<string, unknown>
            | undefined;
          return routeParams && routeParams[paramKey] === params[paramKey];
        }))
    ) {
      found = true;
      break;
    }
    if (!found && route.state?.routes) {
      const didFind = cascade(
        route.state.routes as NavigationState['routes'],
        name,
        params,
      );
      if (didFind) {
        found = true;
      }
    }
  }
  return found;
};

/**
 * Returns true if an exact match for name and params
 * is found anywhere on the navigation stack
 */
export const useNavigationIsSelected = (
  name?: string,
  params?: Record<string, unknown>,
): boolean => {
  const [isSelected, setIsSelected] = React.useState(false);

  const checkIsSelected = React.useCallback(
    (
      routes:
        | NavigationState['routes']
        | PartialRoute<NavigationState['routes'][number]>[]
        | undefined,
    ) => {
      if (routes) {
        setIsSelected(!!name && cascade(routes, name, params));
      }
    },
    [name, params],
  );

  React.useEffect(() => {
    if (name) {
      checkIsSelected(navigationRef.current?.getRootState()?.routes);
      const unsubscribe = navigationRef.current?.addListener('state', (state) =>
        checkIsSelected(state.data.state?.routes),
      );
      return () => {
        unsubscribe?.();
      };
    }
    return () => noop;
  }, [name, params, checkIsSelected]);

  return isSelected;
};

export const useNavigationIsSelectedMulti = (
  screens: {
    name?: string;
    params?: Record<string, unknown>;
  }[],
): boolean => {
  const [isSelected, setIsSelected] = React.useState(false);

  const checkIsSelected = React.useCallback(
    (
      routes:
        | NavigationState['routes']
        | PartialRoute<NavigationState['routes'][number]>[]
        | undefined,
    ) => {
      if (routes) {
        setIsSelected(
          screens.some(
            (screen) =>
              !!screen.name && cascade(routes, screen.name, screen.params),
          ),
        );
      }
    },
    [screens],
  );

  React.useEffect(() => {
    if (screens.length) {
      checkIsSelected(navigationRef.current?.getRootState()?.routes);
      const unsubscribe = navigationRef.current?.addListener('state', (state) =>
        checkIsSelected(state.data.state?.routes),
      );
      return () => {
        unsubscribe?.();
      };
    }
    return () => noop;
  }, [screens, checkIsSelected]);

  return isSelected;
};
